import { getSolanaRpcUrl, getSolscanApiKey } from "./solanaRpc";

export type OnChainTrade = {
  ts: number;
  isBuy: boolean;
  sol: number;
  user: string;
  signature: string;
};

/**
 * Fetches historical trades for a token using Helius Enhanced Transactions API.
 * This is much faster and more accurate than raw RPC polling for historical data.
 */
const historyCache = new Map<string, { data: OnChainTrade[]; ts: number }>();
const HISTORY_TTL = 60_000; // 1 minute

export async function fetchTokenTradeHistory(mint: string, limit = 100): Promise<OnChainTrade[]> {
  const cached = historyCache.get(mint);
  if (cached && Date.now() - cached.ts < HISTORY_TTL) {
    return cached.data;
  }
  
  const rpcUrl = getSolanaRpcUrl();
  let apiKey = "";
  
  try {
    const urlObj = new URL(rpcUrl, window.location.origin);
    apiKey = urlObj.searchParams.get("api-key") || "";
  } catch (e) {
    console.warn("Failed to parse RPC URL for API key:", e);
  }
  
  if (!apiKey) {
    // Fallback to known key from environment
    apiKey = "c78141ce-045d-48ab-9847-405100d54a14";
  }

  const url = `/helius-api/v0/addresses/${mint}/transactions?api-key=${apiKey}`;
  console.log(`[HISTORY] Fetching for ${mint}...`);
  
  // 1. Try Solscan (Primary)
  try {
    const solscanKey = getSolscanApiKey();
    const opts = { headers: { token: solscanKey, "x-lily-app": "1" } };
    const limitStr = `&page=1&page_size=${Math.min(limit, 40)}`;
    
    let res: Response | null = null;
    try {
      res = await fetch(`/solscan-public/token/defi/activities?address=${mint}${limitStr}`, opts);
    } catch (e) {
      console.debug("Solscan public-api failed:", e);
    }
    
    if (!res || res.status === 404 || res.status === 401) {
      try {
        res = await fetch(`/solscan-api-v2/token/defi/activities?address=${mint}${limitStr}`, opts);
      } catch (e) {
        console.debug("Solscan api v2.0 failed:", e);
      }
    }
    
    if (!res || res.status === 401 || res.status === 403) {
      res = await fetch(`/solscan-api-pro/token/defi/activities?address=${mint}${limitStr}`, opts);
    }
    
    if (res.ok) {
      const data = await res.json();
      const items = data.data || [];
      const trades: OnChainTrade[] = items.map((item: any) => {
        const isBuy = item.activity_type === "ACTIVITY_SWAP" && item.to_token_address === mint;
        let solAmount = 0;
        if (item.from_token_address === "So11111111111111111111111111111111111111112") {
           solAmount = (item.from_token_amount || 0) / 1e9;
        } else if (item.to_token_address === "So11111111111111111111111111111111111111112") {
           solAmount = (item.to_token_amount || 0) / 1e9;
        }
        
        return {
          ts: item.block_time * 1000,
          isBuy,
          sol: solAmount,
          user: item.owner || item.from_address,
          signature: item.trans_id
        };
      }).filter((t: OnChainTrade) => t.sol > 0);
      
      if (trades.length > 0) {
        console.log(`[HISTORY] Parsed ${trades.length} trades via Solscan for ${mint}`);
        historyCache.set(mint, { data: trades, ts: Date.now() });
        return trades;
      }
    }
  } catch (solscanErr) {
    console.warn(`[HISTORY] Solscan primary failed for ${mint}, falling back to Helius:`, solscanErr);
  }

  // 2. Try Helius (Fallback)
  let attempts = 0;
  const maxAttempts = 3;
  
  while (attempts < maxAttempts) {
    try {
      const res = await fetch(url);
      
      if (res.status === 429) {
        attempts++;
        const delay = Math.min(1000 * Math.pow(2, attempts), 5000);
        console.warn(`[HISTORY] Helius Rate limited (429) for ${mint}. Retrying in ${delay}ms... (Attempt ${attempts}/${maxAttempts})`);
        await new Promise(r => setTimeout(r, delay));
        continue;
      }
      
      if (!res.ok) throw new Error(`Helius API error: ${res.status}`);
      
      const data = await res.json();
      console.log(`[HISTORY] Found ${data.length} total txs for ${mint} via Helius fallback`);
      const trades: OnChainTrade[] = [];

      for (const tx of data) {
        const swap = tx.events?.swap;
        if (swap) {
          const isBuy = swap.nativeInput !== null && swap.nativeInput !== undefined; 
          const solAmount = isBuy ? swap.nativeInput?.amount : swap.nativeOutput?.amount;
          if (solAmount) {
            trades.push({ ts: tx.timestamp * 1000, isBuy, sol: solAmount / 1e9, user: tx.feePayer, signature: tx.signature });
            continue;
          }
        }

        const transfers = tx.tokenTransfers || [];
        const mintTransfer = transfers.find((tr: any) => tr.mint === mint);
        
        if (mintTransfer) {
          const isBuy = mintTransfer.toUserAccount === tx.feePayer;
          const nativeTransfers = tx.nativeTransfers || [];
          const solChange = nativeTransfers.reduce((acc: number, tr: any) => acc + tr.amount, 0);
          
          if (solChange > 0) {
            trades.push({
              ts: tx.timestamp * 1000,
              isBuy,
              sol: solChange / 1e9,
              user: tx.feePayer,
              signature: tx.signature
            });
            continue;
          }
        }

        if (tx.description && (tx.description.includes("Pump.fun") || tx.description.includes("bought") || tx.description.includes("sold"))) {
           const desc = tx.description;
           const isBuy = desc.includes("bought");
           const solMatch = desc.match(/(\d+\.?\d*)\s+SOL/);
           if (solMatch) {
             trades.push({ ts: tx.timestamp * 1000, isBuy, sol: parseFloat(solMatch[1]), user: tx.feePayer, signature: tx.signature });
           }
        }
      }

      console.log(`[HISTORY] Parsed ${trades.length} trades for ${mint} via Helius`);
      historyCache.set(mint, { data: trades, ts: Date.now() });
      return trades;
    } catch (err) {
      console.error(`[HISTORY] Helius fallback failed for ${mint}:`, err);
      attempts++;
      if (attempts >= maxAttempts) return [];
      await new Promise(r => setTimeout(r, 1000));
    }
  }
  return [];
}
